import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, sessionToken } = useAuth();
  const [settings, setSettings] = useState(null);
  const [classes, setClasses] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClassToDelete, setSelectedClassToDelete] = useState(null);
  const [newClassName, setNewClassName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchClasses();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSettings(response.data);
      setSchoolName(response.data.school_name);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const updateSchoolName = async () => {
    try {
      await axios.put(
        `${API}/settings`,
        { school_name: schoolName },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success('School name updated successfully!');
      setDialogOpen(false);
      fetchSettings();
    } catch (error) {
      console.error('Failed to update school name:', error);
      toast.error('Failed to update school name');
    }
  };

  const createClass = async () => {
    try {
      await axios.post(
        `${API}/classes`,
        { name: newClassName },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success('Class created successfully!');
      setClassDialogOpen(false);
      setNewClassName('');
      fetchClasses();
    } catch (error) {
      console.error('Failed to create class:', error);
      toast.error('Failed to create class');
    }
  };

  const deleteClass = async (classId, className, studentCount) => {
    setSelectedClassToDelete({ id: classId, name: className, students: studentCount });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteClass = async () => {
    if (!selectedClassToDelete) return;
    
    try {
      await axios.delete(`${API}/classes/${selectedClassToDelete.id}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success('Class deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedClassToDelete(null);
      fetchClasses();
    } catch (error) {
      console.error('Failed to delete class:', error);
      toast.error('Failed to delete class');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600 font-medium">Loading settings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <Card className="p-12 rounded-2xl border-slate-100 bg-white text-center">
          <h3 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h3>
          <p className="text-slate-500">Only administrators can access settings.</p>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 font-heading mb-2" data-testid="settings-title">Settings</h1>
          <p className="text-slate-500 text-lg">Manage school information and configuration</p>
        </div>

        {/* School Information */}
        <Card className="p-6 rounded-2xl border-slate-100 bg-white mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-1">School Information</h2>
              <p className="text-slate-500">Basic details about your school</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="edit-school-btn">
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit School Information</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="school_name">School Name</Label>
                    <Input
                      id="school_name"
                      value={schoolName}
                      onChange={(e) => setSchoolName(e.target.value)}
                      className="rounded-xl"
                      data-testid="school-name-input"
                    />
                  </div>
                  <Button
                    onClick={updateSchoolName}
                    className="w-full bg-sky-500 hover:bg-sky-600 rounded-full"
                    data-testid="save-school-btn"
                  >
                    Save Changes
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">School Name</span>
              <span className="font-medium text-slate-900">{settings?.school_name}</span>
            </div>
          </div>
        </Card>

        {/* Classes Management */}
        <Card className="p-6 rounded-2xl border-slate-100 bg-white">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-1">Classes</h2>
              <p className="text-slate-500">Manage school classes</p>
            </div>
            <div className="flex gap-2">
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-full" data-testid="delete-class-main-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Class
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete Class</DialogTitle>
                  </DialogHeader>
                  {selectedClassToDelete ? (
                    <div className="space-y-4">
                      <p className="text-slate-600">
                        Are you sure you want to delete <strong>"{selectedClassToDelete.name}"</strong>?
                        {selectedClassToDelete.students > 0 && (
                          <span className="block mt-2 text-amber-600">
                            ⚠️ This class has {selectedClassToDelete.students} students. They will be unassigned from this class.
                          </span>
                        )}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setDeleteDialogOpen(false)}
                          className="flex-1 rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={confirmDeleteClass}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-slate-600">Select a class to delete from the list below:</p>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {classes.map((cls) => (
                          <button
                            key={cls._id}
                            onClick={() => setSelectedClassToDelete({ id: cls._id, name: cls.name, students: cls.students?.length || 0 })}
                            className="w-full p-3 text-left bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 flex justify-between items-center"
                          >
                            <span className="font-medium">{cls.name}</span>
                            <span className="text-sm text-slate-500">{cls.students?.length || 0} students</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              <Dialog open={classDialogOpen} onOpenChange={setClassDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="add-class-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Class
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Class</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="class_name">Class Name</Label>
                      <Input
                        id="class_name"
                        value={newClassName}
                        onChange={(e) => setNewClassName(e.target.value)}
                        placeholder="e.g., Grade 1-A"
                        className="rounded-xl"
                        data-testid="class-name-input"
                      />
                    </div>
                    <Button
                      onClick={createClass}
                      className="w-full bg-sky-500 hover:bg-sky-600 rounded-full"
                      data-testid="save-class-btn"
                    >
                      Create Class
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {classes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>No classes created yet. Click "Add Class" to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {classes.map((cls) => {
                const studentCount = cls.students?.length || 0;
                return (
                  <div
                    key={cls._id}
                    className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex justify-between items-center"
                    data-testid={`class-item-${cls._id}`}
                  >
                    <div>
                      <h3 className="font-semibold text-slate-900">{cls.name}</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {studentCount} students
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteClass(cls._id, cls.name, studentCount)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-class-${cls._id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default SettingsPage;